import Button from 'ext-ui/components/Button/Button'
import ButtonGroup from 'ext-ui/components/ButtonGroup/ButtonGroup'
import Modal from 'ext-ui/components/Modal/Modal'
import React, { ReactElement } from 'react'
import SvgAppleAddToWallet from 'src/components/Svgs/SvgAppleAddToWallet'
import SvgGoogleAddToWallet from 'src/components/Svgs/SvgGoogleAddToWallet'
import { useTranslate } from 'src/localization/Trns'

import style from './AddToWalletModal.style'

/**
 * One servable wallet. Picking it IS the action — there is no confirm step — so
 * the handler is required, not optional.
 */
export type WalletProvisioningOption = {
  onAdd: () => void
}

export type AddToWalletModalProps = {
  open: boolean
  onClose: () => void
  /**
   * The wallets this card can actually be provisioned to. PRESENCE IS THE
   * SUPPORT VERDICT — an absent wallet renders no badge.
   *
   * TWO entries, not one flag. Apple and Google provision through entirely
   * separate pipelines — Apple's web push-provisioning script and Google's
   * Wallet session — so a card can be servable by one and not the other, and a
   * single combined verdict would offer a badge that cannot complete.
   *
   * Modelled as objects rather than a `supported` boolean beside a separate
   * optional handler so that "supported, but nothing happens on click" cannot be
   * expressed at all. That pairing is the whole invariant: a wallet mark the
   * user can press and get nothing from is worse than no mark.
   */
  apple?: WalletProvisioningOption
  google?: WalletProvisioningOption
}

/**
 * The card hero's "Add to Wallet" dialog (EX-40599).
 *
 * Per the card-management design, picking a wallet IS the action — there is no
 * confirm step and therefore no primary footer button; the footer carries only
 * a dismiss. The badges are Apple's and Google's published artwork, reused from
 * the virtual-card header rather than redrawn, because both vendors require
 * their own mark and forbid restyling it.
 *
 * ⚠️ NO PHYSICAL-CARD PROVISIONING BACKEND EXISTS YET. The web wallet plumbing
 * in this repo (`useGoogleWalletProvisioning`, `initAddToAppleWallet`) is keyed
 * by `virtualCardId` end to end, and `CardManagementAction` has no provisioning
 * verdict, so nothing can currently answer "may this RCN go on a phone?".
 * Callers pass neither wallet until it can, which renders the unsupported state
 * below and never a dead badge.
 */
export default function AddToWalletModal({
  open,
  onClose,
  apple,
  google,
}: AddToWalletModalProps): ReactElement {
  const translate = useTranslate()

  const anySupported = apple !== undefined || google !== undefined

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={translate('Add to mobile wallet')}
      description={translate('Provision this card to a phone for tap-to-pay.')}
      footer={
        <ButtonGroup>
          <Button
            variant="primary"
            onClick={onClose}
            data-testid="add-to-wallet-dismiss"
          >
            {anySupported ? translate('Done') : translate('Close')}
          </Button>
        </ButtonGroup>
      }
    >
      <div css={style}>
        {anySupported ? (
          <div className="AddToWalletModal-wallets">
            {apple !== undefined && (
              <button
                type="button"
                className="AddToWalletModal-badge"
                aria-label={translate('Add to Apple Wallet')}
                onClick={apple.onAdd}
                data-testid="add-to-wallet-apple"
              >
                <SvgAppleAddToWallet aria-hidden />
              </button>
            )}
            {google !== undefined && (
              <button
                type="button"
                className="AddToWalletModal-badge"
                aria-label={translate('Add to Google Wallet')}
                onClick={google.onAdd}
                data-testid="add-to-wallet-google"
              >
                <SvgGoogleAddToWallet aria-hidden />
              </button>
            )}
          </div>
        ) : (
          // Deliberately NOT a `role="status"` live region. This node and its
          // text mount in the same commit the dialog opens in, and a live region
          // that appears already-populated is not reliably announced — the exact
          // finding from the PR #7405 review ("the modal's live region is mounted
          // at the same moment its content appears"). Nothing here updates
          // later, so there is no change to announce: the text is static dialog
          // content, and the dialog's own `aria-describedby` opening
          // announcement plus normal reading order are what carry it.
          <div
            className="AddToWalletModal-unsupported"
            data-testid="add-to-wallet-unsupported"
          >
            <p>
              {translate(
                "This card can't be added to a mobile wallet yet. Your card issuer does not support it for this card.",
              )}
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}
